import { useState, useEffect } from 'react';
import { Key, Globe, BookOpen, FileText, Search, TrendingUp, Layout, Users, Sparkles, Clock } from 'lucide-react';
import { api, Stats, ResourceUsage, Subscription } from '../lib/api';

function formatLimit(limit: number): string {
  if (limit === -1) return 'Unlimited';
  return limit.toLocaleString();
}

function usagePercent(usage: ResourceUsage): number {
  if (usage.limit === -1) return 0;
  if (usage.limit === 0) return 100;
  return Math.min(100, Math.round((usage.current / usage.limit) * 100));
}

function usageColor(pct: number, isUnlimited: boolean): string {
  if (isUnlimited) return 'bg-blue-500';
  if (pct >= 90) return 'bg-red-500';
  if (pct >= 75) return 'bg-amber-500';
  return 'bg-blue-500';
}

function UsageCard({ name, icon: Icon, color, usage }: {
  name: string;
  icon: typeof Key;
  color: string;
  usage: ResourceUsage;
}) {
  const pct = usagePercent(usage);
  const isUnlimited = usage.limit === -1;
  const barColor = usageColor(pct, isUnlimited);

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <div className="flex items-center mb-3">
        <div className={`${color} p-2.5 rounded-lg`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="ml-3">
          <p className="text-sm font-medium text-gray-500">{name}</p>
        </div>
      </div>
      <div className="flex items-baseline gap-1.5 mb-2">
        <span className="text-2xl font-bold text-gray-900">{usage.current.toLocaleString()}</span>
        <span className="text-sm text-gray-400">/</span>
        <span className="text-sm font-medium text-gray-500">{formatLimit(usage.limit)}</span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all ${barColor}`}
          style={{ width: isUnlimited ? '5%' : `${Math.max(pct, 2)}%` }}
        />
      </div>
      {!isUnlimited && pct >= 90 && (
        <p className="text-xs text-red-600 mt-1.5 font-medium">
          {pct >= 100 ? 'Limit reached — upgrade to add more' : 'Approaching limit'}
        </p>
      )}
    </div>
  );
}

export default function Overview({ onNavigate }: { onNavigate?: (tab: string) => void }) {
  const [stats, setStats] = useState<Stats | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsData, subData] = await Promise.all([
        api.getStats(),
        api.getSubscription().catch(() => null),
      ]);
      setStats(statsData);
      setSubscription(subData);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!stats) return null;

  const usageItems = [
    { name: 'Courses', icon: BookOpen, color: 'bg-purple-500', usage: stats.courses },
    { name: 'Lessons', icon: FileText, color: 'bg-orange-500', usage: stats.lessons },
    { name: 'Widgets', icon: Layout, color: 'bg-teal-500', usage: stats.widgets },
    { name: 'API Keys', icon: Key, color: 'bg-blue-500', usage: stats.api_keys },
    { name: 'Domains', icon: Globe, color: 'bg-green-500', usage: stats.domains },
    { name: 'Team Members', icon: Users, color: 'bg-cyan-500', usage: stats.team_members },
    { name: 'Searches (this month)', icon: TrendingUp, color: 'bg-indigo-500', usage: stats.searches_month },
  ];

  const trialDaysRemaining = subscription?.status === 'trialing' && subscription.current_period_end
    ? Math.max(0, Math.ceil((new Date(subscription.current_period_end).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <div className="space-y-6">
      {/* Trial / Subscription banner */}
      {subscription && (
        <div className={`rounded-xl shadow-sm border p-6 ${
          subscription.status === 'trialing'
            ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
            : subscription.status === 'active'
            ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
            : 'bg-white'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${
                subscription.status === 'trialing' ? 'bg-blue-100' : 'bg-green-100'
              }`}>
                {subscription.status === 'trialing' ? (
                  <Clock className="w-6 h-6 text-blue-600" />
                ) : (
                  <Sparkles className="w-6 h-6 text-green-600" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900 capitalize">{subscription.plan} Plan</h3>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    subscription.status === 'trialing'
                      ? 'bg-blue-100 text-blue-700'
                      : subscription.status === 'active'
                      ? 'bg-green-100 text-green-700'
                      : subscription.status === 'past_due'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {subscription.status === 'trialing' ? 'Trial' : subscription.status.replace('_', ' ')}
                  </span>
                </div>
                {subscription.status === 'trialing' && trialDaysRemaining !== null && (
                  <p className="text-sm text-gray-600 mt-0.5">
                    {trialDaysRemaining > 0
                      ? `${trialDaysRemaining} day${trialDaysRemaining !== 1 ? 's' : ''} remaining — trial ends ${new Date(subscription.current_period_end!).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                      : 'Trial has expired'}
                  </p>
                )}
                {subscription.status === 'active' && subscription.current_period_end && (
                  <p className="text-sm text-gray-600 mt-0.5">
                    Renews {new Date(subscription.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                )}
              </div>
            </div>
            {subscription.status === 'trialing' && onNavigate && (
              <button
                onClick={() => onNavigate('billing')}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Upgrade Now
              </button>
            )}
          </div>
        </div>
      )}

      {/* Searches today highlight */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center">
          <div className="bg-pink-500 p-3 rounded-lg">
            <Search className="w-6 h-6 text-white" />
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-500">Searches Today</p>
            <p className="text-2xl font-bold text-gray-900">{(stats.searches_today || 0).toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Usage grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {usageItems.map((item) => (
          <UsageCard key={item.name} {...item} />
        ))}
      </div>

      {/* Quick start */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Start</h3>
        <div className="space-y-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-sm">1</div>
            <div className="ml-4">
              <h4 className="font-medium text-gray-900">Create an API Key</h4>
              <p className="text-sm text-gray-500">Generate an API key to authenticate your search requests.</p>
            </div>
          </div>
          <div className="flex items-start">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-sm">2</div>
            <div className="ml-4">
              <h4 className="font-medium text-gray-900">Add Your Domain</h4>
              <p className="text-sm text-gray-500">Whitelist the domains where your search widget will be used.</p>
            </div>
          </div>
          <div className="flex items-start">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-sm">3</div>
            <div className="ml-4">
              <h4 className="font-medium text-gray-900">Add Your Content</h4>
              <p className="text-sm text-gray-500">Create courses and lessons that will be searchable.</p>
            </div>
          </div>
          <div className="flex items-start">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-sm">4</div>
            <div className="ml-4">
              <h4 className="font-medium text-gray-900">Embed the Widget</h4>
              <p className="text-sm text-gray-500">Add the search widget to your website using our embed code.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
